/**
 * Velada — JavaScript global
 * 1. Utilidades            5. Buscador predictivo
 * 2. Paneles (drawers)     6. Favoritos
 * 3. Carrito AJAX          7. Componentes pequeños
 * 4. Formularios de compra 8. Eventos delegados
 */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};
  const rootUrl = (routes.root_url || '/').replace(/\/?$/, '/');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* 1. Utilidades ---------------------------------------------------------- */
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  let toastTimer;
  function toast(message) {
    const el = document.querySelector('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.hidden = true;
    }, 4500);
  }

  function announce(message) {
    const el = document.querySelector('[data-live-region]');
    if (!el) return;
    el.textContent = '';
    requestAnimationFrame(() => {
      el.textContent = message;
    });
  }

  function setLoading(button, loading) {
    if (!button) return;
    button.classList.toggle('is-loading', loading);
    if (loading) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  function errorMessage(data) {
    if (data && typeof data.description === 'string') return data.description;
    if (data && typeof data.message === 'string') return data.message;
    if (data && typeof data.errors === 'string') return data.errors;
    return strings.cartError;
  }

  // Altura de la cabecera para los elementos "sticky"
  function trackHeaderHeight() {
    const wrapper = document.querySelector('.header-wrapper');
    const root = document.documentElement;
    if (!wrapper || !wrapper.querySelector('.header--sticky')) {
      root.style.setProperty('--header-height', '0px');
      return;
    }
    const update = () => root.style.setProperty('--header-height', `${wrapper.offsetHeight}px`);
    update();
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(wrapper);
  }

  /* 2. Paneles (drawers) y modales ----------------------------------------- */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (!this.isOpen) return;
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.close();
        } else if (event.key === 'Tab') {
          this.trapFocus(event);
        }
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    get panel() {
      return this.querySelector('.drawer__panel') || this;
    }

    open(opener) {
      if (this.isOpen) return;
      document.querySelectorAll('drawer-panel.is-open').forEach((other) => {
        if (other !== this) other.close(false);
      });
      this.opener = opener || document.activeElement;
      if (!this.hasAttribute('role')) {
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.temporaryRole = true;
      }
      this.classList.add('is-open');
      document.documentElement.classList.add('scroll-lock');
      if (this.opener && this.opener.hasAttribute && this.opener.hasAttribute('aria-expanded')) {
        this.opener.setAttribute('aria-expanded', 'true');
      }
      const target = this.querySelector('[data-autofocus]') || this.panel;
      setTimeout(() => target.focus({ preventScroll: true }), 60);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      if (this.temporaryRole) {
        this.removeAttribute('role');
        this.removeAttribute('aria-modal');
        this.temporaryRole = false;
      }
      if (!document.querySelector('drawer-panel.is-open')) {
        document.documentElement.classList.remove('scroll-lock');
      }
      const opener = this.opener;
      if (opener && opener.hasAttribute && opener.hasAttribute('aria-expanded')) {
        opener.setAttribute('aria-expanded', 'false');
      }
      if (restoreFocus && opener && document.contains(opener) && typeof opener.focus === 'function') {
        opener.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    trapFocus(event) {
      const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0
      );
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define('drawer-panel', DrawerPanel);

  /* 3. Carrito AJAX --------------------------------------------------------- */
  const cart = {
    get onCartPage() {
      return document.body.classList.contains('template-cart');
    },

    sectionIds() {
      return [...document.querySelectorAll('[data-cart-section]')].map((el) => el.dataset.cartSection);
    },

    render(sections) {
      if (!sections) return;
      document.querySelectorAll('[data-cart-section]').forEach((el) => {
        const html = sections[el.dataset.cartSection];
        if (!html) return;
        const fresh = parseHTML(html).querySelector('[data-cart-section]');
        if (fresh) el.innerHTML = fresh.innerHTML;
      });
      const counter = document.querySelector('[data-cart-section] [data-cart-count]');
      if (counter) this.setCount(parseInt(counter.dataset.cartCount, 10) || 0);
    },

    setCount(count) {
      document.querySelectorAll('[data-cart-bubble]').forEach((bubble) => {
        bubble.textContent = count;
        bubble.hidden = count === 0;
      });
    },

    open(opener) {
      if (this.onCartPage) return;
      const drawer = document.getElementById('CartDrawer');
      if (drawer && typeof drawer.open === 'function') drawer.open(opener);
    },

    async refresh() {
      const ids = this.sectionIds();
      if (!ids.length) return;
      const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
      if (response.ok) this.render(await response.json());
    },

    async add(formData, { withSections = true } = {}) {
      if (withSections) {
        formData.append('sections', this.sectionIds().join(','));
        formData.append('sections_url', window.location.pathname);
      }
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: formData
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(errorMessage(data));
      return data;
    },

    async change(key, quantity, trigger) {
      const regions = document.querySelectorAll('[data-cart-section]');
      const region = trigger ? trigger.closest('[data-cart-section]') : null;
      regions.forEach((el) => el.classList.add('is-updating'));
      try {
        const response = await fetch(routes.cart_change_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript'
          },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: this.sectionIds(),
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();
        if (!response.ok || data.status || data.errors) throw new Error(errorMessage(data));
        this.render(data.sections);
      } catch (error) {
        toast(error.message || strings.cartError);
        try {
          await this.refresh();
        } catch (refreshError) {
          window.location.reload();
        }
      } finally {
        regions.forEach((el) => el.classList.remove('is-updating'));
        // Devolver el foco a la misma línea o, si ya no existe, al panel
        if (region) {
          const input = [...region.querySelectorAll('[data-cart-qty]')].find((el) => el.dataset.cartQty === key);
          const fallback = region.matches('.drawer__panel') ? region : region.querySelector('h1, a, button');
          const target = input || fallback;
          if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
        }
      }
    }
  };
  theme.cart = cart;

  /* 4. Formularios de compra (página de producto, tarjetas, sugerencias) ---- */
  async function onProductFormSubmit(event) {
    const form = event.target.closest('form[data-product-form]');
    if (!form) return;
    event.preventDefault();

    const submitter = event.submitter || form.querySelector('[type="submit"]');
    if (submitter && submitter.classList.contains('is-loading')) return;

    const buyNow = Boolean(submitter && submitter.hasAttribute('data-buy-now'));
    const formData = new FormData(form);
    if (submitter && submitter.name === 'id') formData.set('id', submitter.value);
    formData.delete('return_to');

    const errorBox = form.querySelector('[data-form-error]');
    if (errorBox) errorBox.hidden = true;
    setLoading(submitter, true);

    try {
      const data = await cart.add(formData, { withSections: !buyNow });
      if (buyNow) {
        window.location.href = routes.checkout_url || '/checkout';
        return;
      }
      cart.render(data.sections);
      const details = form.closest('details.quick-add__details');
      if (details) details.removeAttribute('open');
      announce(strings.added);
      cart.open(document.contains(submitter) ? submitter : null);
      if (cart.onCartPage) toast(strings.added);
    } catch (error) {
      const message = error.message || strings.cartError;
      if (errorBox) {
        errorBox.textContent = message;
        errorBox.hidden = false;
      } else {
        toast(message);
      }
    }
    setLoading(submitter, false);
  }

  /* 5. Buscador predictivo --------------------------------------------------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (this.dataset.enabled !== 'true' || !this.input || !this.results) return;
      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.search(), 250);
      });
    }

    async search() {
      const query = this.input.value.trim();
      if (this.controller) this.controller.abort();
      if (query.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        section_id: 'predictive-search'
      });
      try {
        const response = await fetch(`${routes.predictive_search_url}?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const section = parseHTML(await response.text()).querySelector('#shopify-section-predictive-search');
        this.results.innerHTML = section ? section.innerHTML.trim() : '';
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* 6. Favoritos (se guardan en el navegador) -------------------------------- */
  const wishlist = {
    key: 'theme:wishlist',

    get() {
      try {
        const list = JSON.parse(window.localStorage.getItem(this.key));
        return Array.isArray(list) ? list : [];
      } catch (error) {
        return [];
      }
    },

    set(list) {
      try {
        window.localStorage.setItem(this.key, JSON.stringify(list));
      } catch (error) {
        /* almacenamiento no disponible (navegación privada): se ignora */
      }
      this.sync();
      document.dispatchEvent(new CustomEvent('wishlist:change'));
    },

    toggle(handle) {
      const list = this.get();
      const index = list.indexOf(handle);
      if (index > -1) {
        list.splice(index, 1);
      } else {
        list.unshift(handle);
      }
      this.set(list);
    },

    remove(handle) {
      this.set(this.get().filter((item) => item !== handle));
    },

    sync() {
      const list = this.get();
      document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
        const active = list.includes(button.dataset.wishlistToggle);
        const pressed = String(active);
        if (button.getAttribute('aria-pressed') !== pressed) {
          button.setAttribute('aria-pressed', pressed);
          button.setAttribute('aria-label', active ? strings.wishlistRemove : strings.wishlistAdd);
        }
      });
      document.querySelectorAll('[data-wishlist-bubble]').forEach((bubble) => {
        const text = String(list.length);
        if (bubble.textContent !== text) bubble.textContent = text;
        bubble.hidden = list.length === 0;
      });
    }
  };

  class WishlistList extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.drawer = this.closest('drawer-panel');
      if (!this.grid || !this.drawer) return;
      this.drawer.addEventListener('drawer:open', () => this.load());
      this.onChange = () => {
        if (this.drawer.isOpen) this.prune();
      };
      document.addEventListener('wishlist:change', this.onChange);
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:change', this.onChange);
    }

    prune() {
      const handles = wishlist.get();
      [...this.grid.children].forEach((item) => {
        if (!handles.includes(item.dataset.handle)) item.remove();
      });
      if (this.empty) this.empty.hidden = handles.length > 0;
    }

    load() {
      this.prune();
      wishlist.get().forEach((handle) => {
        if ([...this.grid.children].some((item) => item.dataset.handle === handle)) return;
        const item = document.createElement('li');
        item.dataset.handle = handle;
        this.grid.append(item);
        fetch(`${rootUrl}products/${encodeURIComponent(handle)}?section_id=product-card-ajax`)
          .then((response) => {
            if (response.status === 404) {
              wishlist.remove(handle);
              throw new Error('not-found');
            }
            if (!response.ok) throw new Error(String(response.status));
            return response.text();
          })
          .then((html) => {
            const card = parseHTML(html).querySelector('.product-card');
            if (!card) throw new Error('empty');
            item.append(card);
            wishlist.sync();
          })
          .catch(() => item.remove());
      });
    }
  }
  customElements.define('wishlist-list', WishlistList);

  /* 7. Componentes pequeños -------------------------------------------------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-announcement]')];
      const interval = parseInt(this.dataset.interval, 10);
      if (!interval || this.items.length < 2 || reducedMotion.matches) return;
      this.index = 0;
      this.timer = setInterval(() => this.show((this.index + 1) % this.items.length), interval);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    show(index) {
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
      this.index = index;
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.loaded) return;
      const load = () => {
        this.loaded = true;
        fetch(this.dataset.url)
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => {
            const fresh = parseHTML(html).querySelector('product-recommendations');
            if (fresh && fresh.querySelector('.product-card')) {
              this.innerHTML = fresh.innerHTML;
              this.hidden = false;
              wishlist.sync();
            }
          })
          .catch(() => {});
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            observer.disconnect();
            load();
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        // El elemento está oculto hasta tener contenido: se observa su sección
        observer.observe(this.parentElement || this);
      } else {
        load();
      }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* 8. Eventos delegados ----------------------------------------------------- */
  document.addEventListener('submit', onProductFormSubmit);

  document.addEventListener('click', (event) => {
    const target = event.target;

    // Abrir paneles
    const opener = target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (!drawer || typeof drawer.open !== 'function') return;
      if (drawer.id === 'CartDrawer' && cart.onCartPage) return;
      event.preventDefault();
      drawer.open(opener);
      return;
    }

    // Botones + / − de cantidad
    const qtyButton = target.closest('[data-qty-change]');
    if (qtyButton) {
      const input = qtyButton.parentElement.querySelector('input');
      if (!input) return;
      const step = parseInt(input.step, 10) || 1;
      const min = input.min !== '' ? parseInt(input.min, 10) : 0;
      const max = input.max !== '' ? parseInt(input.max, 10) : Infinity;
      const current = parseInt(input.value, 10) || 0;
      const next = Math.min(max, Math.max(min, current + step * parseInt(qtyButton.dataset.qtyChange, 10)));
      if (next !== current) {
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    // Eliminar una línea del carrito
    const remove = target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      cart.change(remove.dataset.cartRemove, 0, remove);
      return;
    }

    // Favoritos
    const heart = target.closest('[data-wishlist-toggle]');
    if (heart) {
      event.preventDefault();
      wishlist.toggle(heart.dataset.wishlistToggle);
      return;
    }

    // Cerrar la compra rápida al hacer clic fuera
    document.querySelectorAll('details.quick-add__details[open]').forEach((details) => {
      if (!details.contains(target)) details.removeAttribute('open');
    });
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target.matches('[data-cart-qty]')) {
      const quantity = parseInt(target.value, 10);
      if (Number.isNaN(quantity)) {
        // Campo vacío: se recupera la cantidad anterior en lugar de borrar la línea
        target.value = target.defaultValue;
        return;
      }
      cart.change(target.dataset.cartQty, Math.max(0, quantity), target);
    } else if (target.matches('[data-auto-submit]') && target.form) {
      target.form.submit();
    }
  });

  // Editor de temas: mostrar el mensaje de la barra de anuncios que se está editando
  document.addEventListener('shopify:block:select', (event) => {
    const message = event.target.closest('[data-announcement]');
    const bar = message ? message.closest('announcement-bar') : null;
    if (!bar || !bar.items) return;
    clearInterval(bar.timer);
    bar.show(bar.items.indexOf(message));
  });

  // Los favoritos se vuelven a marcar cuando llega contenido nuevo por AJAX
  let syncFrame;
  new MutationObserver(() => {
    cancelAnimationFrame(syncFrame);
    syncFrame = requestAnimationFrame(() => wishlist.sync());
  }).observe(document.body, { childList: true, subtree: true });

  trackHeaderHeight();
  wishlist.sync();
})();
